import React, { useCallback, useEffect, useState } from 'react';
import { Card, Col, Row, Typography, Divider, Tabs, Button, Modal, message, Spin } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { RiStarLine, RiHeartLine, RiCheckLine, RiUserLine, RiFileListLine } from "react-icons/ri";
import { AiFillDelete } from "react-icons/ai";
import { MdOutlineLogout } from "react-icons/md";
import ProfileAvatar from '../../components/profiles/ProfileAvatar';
import ProfileTab from '../../components/profiles/ProfileTab';
import MemberCard from '../../components/profiles/MemberCard';
import DownloadApp from '../../components/DownloadApp';
import { useTranslation } from 'react-i18next';
import FavMagazinePage from '../magazine/FavMagazinePage';
import LikeMagazinePage from '../magazine/LikeMagazinePage';
import { debounce } from 'lodash';
import { loginSuccessAction } from '../../modules/auth/actions';
import { storageKeys } from '../../constants/storage';
import { DELETE_ACCOUNT_URL, PROFILE_INFO_URL } from '../../constants/Url';
import httpClient from '../../utils/HttpClient';
import { handleLogout } from '../../utils/storage';
import { useDispatch, useSelector } from 'react-redux';
import LikeCoursePage from '../education/LikeCoursePage';
import PurchasedCoursePage from '../education/PurchasedCoursePage';
import MyOrdersPage from '../orders/MyOrdersPage';
import PurchaseHistoryPage from '../orders/PurchaseHistoryPage';
import MyActivitiesPage from './MyActivitiesPage';
import { LuHistory } from "react-icons/lu";
import FailSvg from '../../assets/icons/FailIcon';
import EventFlagIcon from '../../assets/icons/EventFlag';
const { Title, Text } = Typography;


const ProfilePage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const getInitialTab = useCallback(() => {
        const params = new URLSearchParams(location.search);
        const tab = params.get('tab');
        if (tab) return tab;

        const path = location.pathname;
        if (path.includes('fav-magazine')) return 'favourites';
        if (path.includes('like-magazine')) return 'likes';
        if (path.includes('like-course')) return 'likes-course';
        if (path.includes('purchased-course')) return 'purchased-course';
        if (path.includes('my-orders')) return 'my-orders';
        if (path.includes('my-activities')) return 'my-activities';
        if (path.includes('purchase-history')) return 'purchase-history';
        if (path.includes('profiles')) return 'profile';
        
        return 'favourites';
    }, [location]);

    const [activeTab, setActiveTab] = useState(getInitialTab);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    useEffect(() => {
        setActiveTab(getInitialTab());
    }, [getInitialTab]);

    const user = useSelector((state) => state.authReducer);
    let token = user && user?.token ? user?.token : null
    const [avatarUrl, setAvatarUrl] = useState(null);
    const [profile, setProfile] = useState([]);
    const [profileLoading, setProfileLoading] = useState(true);
    const dispatch = useDispatch();

    const NAV_ITEMS = [
        { key: 'favourites', icon: <RiStarLine size={20} />, label: t('text_fav_mazagine') },
        { key: 'likes', icon: <RiHeartLine size={20} />, label: t('text_like_mazagine') },
        { key: 'likes-course', icon: <RiHeartLine size={20} />, label: t('text_like_course') },
        { key: 'purchased-course', icon: <RiCheckLine size={20} />, label: t('text_purchase_course') },
        { key: 'my-orders', icon: <RiFileListLine size={20} />, label: t('text_my_order') },
        { key: 'my-activities', icon: <EventFlagIcon size={20} />, label: t('text_my_activities') },
        // { key: 'purchase-history', icon: <LuHistory size={20} />, label: t('text_purchase_history') },
        { key: 'profile', icon: <RiUserLine size={20} />, label: t('text_profile') }
    ];

    const saveAuth = (token, user, refreshToken) => {
        dispatch(loginSuccessAction({ token, user: user, photo: user?.picUrl }));
        localStorage.setItem(storageKeys.TOKEN, btoa(token));
        localStorage.setItem(storageKeys.USER, JSON.stringify(user));
    };

    const fetchProfiles = async () => {
        if(activeTab === "profile") {
            setProfileLoading(true);
        }

        try {
            const res = await httpClient.get(PROFILE_INFO_URL);
            const member = res?.data?.member;
            const status = res?.data?.status;

            if (status === 200) {
                setProfile(member);
                saveAuth(token, member)
            } else {
                setProfile([]);
            }

        } catch (error) {
            console.error("fetchData -> error", error);
            setProfile([]);
        } finally {
            setProfileLoading(false);
        }
    };
    const debouncedFetchAll = useCallback(
        debounce(async () => {
            await Promise.all([fetchProfiles()])
            setRefreshTrigger(prev => prev + 1);
        }, 300),
        []
    );

    useEffect(() => {
        debouncedFetchAll();
        return debouncedFetchAll.cancel;
    }, [debouncedFetchAll])

    useEffect(() => {
        fetchProfiles()
    }, [activeTab])

    const tabItems = [
        {
            key: 'favourites',
            children: (
                <div style={{ padding: '8px 4px' }}>
                    <Title level={4} style={{ margin: 0, marginBottom: 20 }}>{t('text_fav_mazagine')}</Title>
                    <FavMagazinePage/>
                </div>
            )
        },
        {
            key: 'likes',
            children: (
                <div style={{ padding: '8px 4px' }}>
                    <Title level={4} style={{ margin: 0, marginBottom: 20 }}>{t('text_like_mazagine')}</Title>
                    <LikeMagazinePage isHide={true}/>
                </div>
            )
        },
        {
            key: 'likes-course',
            children: (
                <div style={{ padding: '8px 4px' }}>
                    <Title level={4} style={{ margin: 0, marginBottom: 20 }}>{t('text_like_course')}</Title>
                    <LikeCoursePage isHide={true}/>
                </div>
            )
        },
        {
            key: 'purchased-course',
            children: (
                <div style={{ padding: '8px 4px' }}>
                    <Title level={4} style={{ margin: 0, marginBottom: 20 }}>{t('text_purchase_course')}</Title>
                    <PurchasedCoursePage isHide={true}/>
                </div>
            )
        },
        {
            key: 'my-orders',
            children: (
                <div style={{ padding: '8px 4px' }}>
                    <Title level={4} style={{ margin: 0, marginBottom: 20 }}>{t('text_my_order')}</Title>
                    <MyOrdersPage  isHide={true}/>
                </div>
            )
        },
        {
            key: 'my-activities',
            children: (
                <div style={{ padding: '8px 4px' }}>
                    <MyActivitiesPage isHide={true} />
                </div>
            )
        },
        {
            key: 'purchase-history',
            children: (
                <div style={{ padding: '8px 4px' }}>
                    <Title level={4} style={{ margin: 0, marginBottom: 20 }}>{t('text_purchase_history')}</Title>
                    <PurchaseHistoryPage isHide={true} refreshTrigger={refreshTrigger}/>
                </div>
            )
        },
        {
            key: 'profile',
            children: (
                <ProfileTab
                    profile={profile}
                    avatarUrl={avatarUrl}
                    onAvatarChange={setAvatarUrl}
                    onProfileUpdate={(updated) => setProfile((p) => ({ ...p, ...updated }))}
                    debouncedFetchAll={debouncedFetchAll}
                />
            ),
        },
    ];

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const onLogout = () => {
        handleLogout();
        navigate('/');
    };

    const handleConfirmDelete = async () => {
        try {
            setDeleteLoading(true);
            await httpClient.delete(DELETE_ACCOUNT_URL);
            message.success(t("Account deleted successfully"));
            setIsDeleteModalOpen(false);
            handleLogout();
        } catch (error) {
            console.error(error);
            message.error(t("Failed to delete account"));
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <div className='esi-std-container- full-flex pb-[40px]' style={{ minHeight: '100vh', paddingTop: '32px' }}>
            {<Spin spinning={profileLoading}>
                <Row gutter={24} style={{ maxWidth: 1215, margin: '0 auto' }}>

                    {/* ── Sidebar ── */}
                    <Col xs={24} lg={7} style={{ marginBottom: 24 }}>
                        <Card bordered={false} style={{ borderRadius: 16, textAlign: 'center', paddingTop: 8 }}>
                            <div style={{ marginBottom: 10 }}>
                                <ProfileAvatar
                                    name={`${profile?.name}`}
                                    size={72}
                                    imageUrl={profile?.picUrl ? profile?.picUrl : avatarUrl}
                                    onImageChange={setAvatarUrl}
                                    debouncedFetchAll={debouncedFetchAll}
                                />
                            </div>
                            <div style={{ fontWeight: 700, fontSize: 16 }}>
                                {profile?.name}
                            </div>
                            <Text type="secondary" style={{ fontSize: 13, marginBottom: 12, display: 'block' }}>{profile.email}</Text>
                            <MemberCard profile={profile} onRefresh={debouncedFetchAll} />

                            <Divider style={{ margin: '18px 0 10px' }} />

                            <div style={{ textAlign: 'left', padding: '0 12px' }}>
                                {NAV_ITEMS.map((item) => (
                                    <div
                                        key={item.key}
                                        onClick={() => setActiveTab(item.key)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 12,
                                            padding: '10px 14px',
                                            borderRadius: 10,
                                            cursor: 'pointer',
                                            marginBottom: 4,
                                            background: activeTab === item.key ? '#fdecf1' : 'transparent',
                                            color: activeTab === item.key ? '#e50a47' : '#555',
                                            fontWeight: activeTab === item.key ? 600 : 400,
                                            transition: 'all 0.2s',
                                        }}
                                    >
                                        {item.icon} {item.label}
                                    </div>
                                ))}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '1.25rem', padding: '0 12px' }}>
                                <Button 
                                    onClick={onLogout} 
                                    icon={<MdOutlineLogout size={18} />} 
                                    size='large' 
                                    style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center', 
                                        gap: 8,
                                        backgroundColor: "#21163e", 
                                        color: "white", 
                                        border: "none",
                                        borderRadius: 8,
                                        width: '100%'
                                    }}
                                >
                                    {t('text_logout')}
                                </Button>
                                {/* <Button 
                                    onClick={() => setIsDeleteModalOpen(true)} 
                                    icon={<AiFillDelete size={18} />} 
                                    size='large' 
                                    style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center', 
                                        gap: 8,
                                        backgroundColor: "#dc2626", 
                                        color: "white", 
                                        border: "none",
                                        borderRadius: 8,
                                        width: '100%',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {t('text_request_delete_account')}
                                </Button> */}
                            </div>
                        </Card>
                    </Col>

                    {/* ── Main content ── */}
                    <Col xs={24} lg={17}>
                        <Card bordered={false} style={{ borderRadius: 16, minHeight: 480 }}>
                            <Tabs
                                activeKey={activeTab}
                                onChange={setActiveTab}
                                items={tabItems}
                                tabBarStyle={{ marginBottom: 0, borderBottom: "none", borderTop: "none" }}
                            />
                        </Card>
                    </Col>
                </Row>
            </Spin>}

            {/* ── Custom Centered Delete Account Confirmation Modal ── */}
            <Modal
                open={isDeleteModalOpen}
                onCancel={() => !deleteLoading && setIsDeleteModalOpen(false)}
                footer={null}
                centered
                width={420}
                closable={!deleteLoading}
                maskClosable={!deleteLoading}
                styles={{
                    content: {
                        borderRadius: 20,
                        padding: '32px 28px 24px',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    }
                }}
            >
                <div style={{ textAlign: 'center' }}>
                    {/* Visual Danger Icon Badge */}
                    <div
                        style={{
                            width: 64,
                            height: 64,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 10px',
                        }}
                    >
                        <FailSvg />
                    </div>

                    {/* Title */}
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 30, lineHeight: 1.4 }}>
                        {t("text_confirm_delete_account")}
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: 12 }}>
                        <Button
                            size="large"
                            disabled={deleteLoading}
                            onClick={() => setIsDeleteModalOpen(false)}
                            style={{
                                flex: 1,
                                height: 44,
                                borderRadius: 10,
                                fontWeight: 600,
                                borderColor: '#E5E7EB',
                                color: '#374151'
                            }}
                        >
                            {t("Cancel")}
                        </Button>
                        <Button
                            danger
                            type="primary"
                            size="large"
                            loading={deleteLoading}
                            onClick={handleConfirmDelete}
                            style={{
                                flex: 1,
                                height: 44,
                                borderRadius: 10,
                                fontWeight: 600,
                                backgroundColor: '#DC2626',
                                borderColor: '#DC2626',
                                boxShadow: '0 4px 6px -1px rgba(220, 38, 38, 0.25)'
                            }}
                        >
                            {t("Ok")}
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default ProfilePage;
